import React from 'react';
import { Switch } from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

const ProductDetailsTab = ({ 
  register, 
  errors, 
  watch, 
  setValue, 
  inputClassName,
  productCategories, 
  manufacturers, 
  equipmentTypes,
  extWarrantyProviders,
  trigger
}) => {
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-3">
      <div className="px-6 py-4  from-blue-50 to-indigo-50 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <InformationCircleIcon className="h-6 w-6 mr-2 text-[#009efb]" />
          Product Details
        </h2>
      </div>
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Product Category <span class="text-red-500">*</span></label>
           
            <select
              {...register('product_category_id')}
              className={`${inputClassName} ${
                errors.product_category_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              value={watch('product_category_id') || ''}
            > 
              <option value="">Select Product Category</option>
              {productCategories?.data?.map((category) => (
              
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            
            {errors.product_category_id  && (
              <p className="mt-1 text-sm text-red-600">{errors.product_category_id.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Product Name <span class="text-red-500">*</span></label>
            <input
              type="text"
              {...register('name')}
              className={`${inputClassName} ${
                errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter product name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">Long Description</label>
          <textarea
            {...register('description')}
            rows="4"
            className={`${inputClassName} ${
              errors.description ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
            }`}
            placeholder="Enter detailed product description"
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
          )}
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Model</label>
            <input
              type="text"
              {...register('model')}
              className={`${inputClassName} ${
                errors.model ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter model number"
            />
            {errors.model && (
              <p className="mt-1 text-sm text-red-600">{errors.model.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Part Number</label>
            <input
              type="text"
              {...register('part_number')}
              className={`${inputClassName} ${
                errors.part_number ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter part number"
            />
            {errors.part_number && (
              <p className="mt-1 text-sm text-red-600">{errors.part_number.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">SKU</label>
            <input
              type="text"
              {...register('sku')}
              className={`${inputClassName} ${
                errors.sku ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter SKU"
            />
            {errors.sku && (
              <p className="mt-1 text-sm text-red-600">{errors.sku.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">UPC</label>
            <input
              type="text"
              {...register('upc')}
              className={`${inputClassName} ${
                errors.upc ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter UPC code"
            />
            {errors.upc && (
              <p className="mt-1 text-sm text-red-600">{errors.upc.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Type</label>
            <input
              type="text"
              {...register('type')}
              className={`${inputClassName} ${
                errors.type ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter product type"
            />
            {errors.type && (
              <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product Status</label>
            <Switch
              checked={watch('active')}
              onChange={(value) => setValue('active', value)}
              className={`${
                watch('active') ? 'bg-[#009efb]' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#009efb] focus:ring-offset-2`}
            >
              <span
                className={`${
                  watch('active') ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Equipment Item</label>
            <Switch
              checked={watch('is_equipment_item')}
              onChange={(value) => setValue('is_equipment_item', value)}
              className={`${
                watch('is_equipment_item') ? 'bg-[#009efb]' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#009efb] focus:ring-offset-2`}
            >
              <span
                className={`${
                  watch('is_equipment_item') ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
          </div>
        </div>
 
        {/* Equipment Item Conditional Fields */}
        {watch('is_equipment_item') && (
          <div className="space-y-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-lg font-medium text-blue-900">Equipment Details</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Manufacturer <span class="text-red-500">*</span></label>
                <select
                  {...register('manufacturer_id')}
                  className={`${inputClassName} ${
                    errors.manufacturer_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  value={watch('manufacturer_id') || ''}
                >
                  <option value="">Select Manufacturer</option>
                  {manufacturers?.data?.map((manufacturer) => (
                    <option key={manufacturer.id} value={manufacturer.id}>
                      {manufacturer.name}
                    </option>
                  ))}
                </select>
                {errors.manufacturer_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.manufacturer_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Equipment Type <span class="text-red-500">*</span></label>
                <select
                  {...register('equipment_type_id')}
                  className={`${inputClassName} ${
                    errors.equipment_type_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  value={watch('equipment_type_id') || ''}
                >
                  <option value="">Select Equipment Type</option>
                  {equipmentTypes?.data?.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {errors.equipment_type_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.equipment_type_id.message}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Extended Warranty Section */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Extended Warranty</label>
            <Switch
              checked={watch('is_extended_warranty')}
              onChange={(value) => setValue('is_extended_warranty', value)}
              className={`${
                watch('is_extended_warranty') ? 'bg-[#009efb]' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#009efb] focus:ring-offset-2`}
            >
              <span
                className={`${
                  watch('is_extended_warranty') ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
          </div>
          
          {watch('is_extended_warranty') && (
            <div className="space-y-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="text-lg font-medium text-green-900">Warranty Information</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Warranty Provider <span class="text-red-500">*</span></label>
                  <select
                    {...register('warranty_provider_id')}
                    className={`${inputClassName} ${
                      errors.warranty_provider_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                    value={watch('warranty_provider_id') || ''}
                  >
                    <option value="">Select Warranty Provider</option>
                    {extWarrantyProviders?.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </select>
                  {errors.warranty_provider_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.warranty_provider_id.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Warranty Expires <span class="text-red-500">*</span></label>
                  <input
                    type="text"
                    {...register('warranty_expires')}
                    className={`datepicker ${inputClassName} ${
                      errors.warranty_expires ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                    onInput={(e) => {
                      
                      setValue('warranty_expires', e.target.value);
                      trigger('warranty_expires');
                      
                    }}
                    
                    placeholder="Enter Warranty Expires"
                  />
                  {errors.warranty_expires && (
                    <p className="mt-1 text-sm text-red-600">{errors.warranty_expires.message}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsTab; 